// features/admin/agentDetailsApi.ts
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

// ─── Shared / Primitive Types ─────────────────────────────────────────────────

export type DocStatus = 'approved' | 'pending' | 'rejected';
export type AssignmentType = 'dispatch' | 'return';
export type AssignmentStatus = 'assigned' | 'picked_up' | 'completed' | 'cancelled';

// ─── Agent Monitor List ───────────────────────────────────────────────────────

export interface AgentProfile {
  agent_id: number;
  full_name: string;
  email: string;
  contact: string;
  address: string;
  nationality: string;
  profile_photo: string | null;
  joined_date: string;
  safety_name: string | null;
  safety_contact: string | null;
}

export interface DocumentEntry {
  no: string | null;
  validity: string | null;
  status: DocStatus;
  status_code: number;
  reject_reason: string | null;
}

export interface AgentDocuments {
  licence: DocumentEntry;
  vehicle_registration: DocumentEntry;
  id_proof: DocumentEntry;
  passport: DocumentEntry;
}

export interface AgentAvailability {
  active: boolean;
  is_online: boolean;
  status_label: string;
  current_load: number;
  max_capacity: number;
  load_percent: number | null;
  at_capacity: boolean;
}

export interface AgentLocation {
  latitude: string | null;
  longitude: string | null;
  last_updated_at: string | null;
  google_maps_link: string | null;
}

export interface CurrentOrder {
  order_id: number;
  invoice_no: string;
  order_status: string;
  order_status_code: number;
  amount: number;
  cash_on_delivery: boolean;
  pay_later: boolean;
  pickup_code: string | null;
  customer_name: string;
  customer_contact: string;
  delivery_address: string;
  city: string;
  destination: {
    lat: string | null;
    lng: string | null;
    google_maps_link: string | null;
  };
  assigned_at: string;
  picked_up_at: string | null;
  elapsed_minutes: number;
  sla_minutes: number;
  sla_breached: boolean;
}

export interface TodayStats {
  assigned: number;
  delivered: number;
  failed: number;
  out_for_delivery: number;
  in_transit: number;
  avg_delivery_min: number;
  ontime: number;
  ontime_pct: number;
  success_rate: number;
  revenue: number;
  daily_target: number;
  target_progress_pct: number | null;
}

export interface PeriodStats {
  from_date: string;
  to_date: string;
  assigned: number;
  delivered: number;
  failed: number;
  cancelled: number;
  avg_delivery_min: number;
  ontime: number;
  ontime_pct: number;
  success_rate: number;
  revenue: number;
  first_order_date: string | null;
  last_order_date: string | null;
}

export interface AllTimePerformance {
  total_assigned: number;
  total_delivered: number;
  total_failed: number;
  total_cancelled: number;
  avg_delivery_min: number;
  total_ontime: number;
  ontime_pct: number;
  success_rate: number;
  total_revenue: number;
  first_delivery_date: string | null;
  last_delivery_date: string | null;
  rating: string;
}

export interface AssignmentCod {
  amount: number | null;
  payment_mode: string | null;
  collected: boolean;
}

export interface AssignmentRecord {
  assignment_id: string;
  order_id: number;
  reference_id: string | null;
  assignment_type: AssignmentType;
  status: AssignmentStatus;
  warehouse_completed: boolean;
  warehouse_completed_time: string | null;
  assigned_at: string;
  pickup_time: string | null;
  delivered_time: string | null;
  returned_time: string | null;
  proof_image: string | null;
  notes: string | null;
  return_type: string | null;
  order_type: string;
  cod: AssignmentCod;
  open_box_verified: boolean;
  elapsed_minutes: number;
  is_ontime: boolean;
  sla_breached: boolean;
}

export interface AssignmentSummary {
  total: number;
  completed: number;
  picked_up: number;
  assigned: number;
  cancelled: number;
  dispatches: number;
  returns: number;
  cod_collected_count: number;
  cod_total_amount: number;
  open_box_verified_count: number;
  ontime_count: number;
  ontime_pct: number;
}

export interface AgentNotification {
  notif_id: number;
  order_id: number | null;
  title: string;
  type: string;
  read_flag: number;
  sent_at: string;
}

export interface AgentMonitorEntry {
  profile: AgentProfile;
  documents: AgentDocuments;
  availability: AgentAvailability;
  location: AgentLocation;
  current_order: CurrentOrder | null;
  today: TodayStats;
  period: PeriodStats;
  performance: AllTimePerformance;
  assignments: {
    summary: AssignmentSummary;
    records: AssignmentRecord[];
  };
  notifications: {
    unread_count: number;
    recent: AgentNotification[];
  };
}

export interface FleetSummary {
  total_agents: number;
  active_agents: number;
  inactive_agents: number;
  online_now: number;
  offline_now: number;
  agents_at_capacity: number;
  avg_current_load: number;
}

export interface AgentMonitorSummary {
  fleet: FleetSummary;
  documents: {
    licence_approved: number;
    licence_pending: number;
    licence_rejected: number;
    vreg_approved: number;
    vreg_pending: number;
    id_approved: number;
    id_pending: number;
  };
  today: {
    total_orders: number;
    delivered: number;
    failed: number;
    out_for_delivery: number;
    in_transit: number;
    avg_delivery_minutes: number;
    ontime_deliveries: number;
    ontime_pct: number;
    today_revenue: number;
    daily_target: number;
    target_progress_pct: number | null;
  };
}

export interface AgentMonitorListRequest {
  agent_id?: number;
  active?: 0 | 1;
  online?: 0 | 1;
  licence_status?: 0 | 1 | 2;
  search?: string;
  from_date?: string;
  to_date?: string;
  page?: number;
  limit?: number;
  download?: 0 | 1 | 2;
}

export interface AgentMonitorListResponse {
  error: boolean;
  message: string;
  summary: AgentMonitorSummary;
  settings: {
    sla_minutes: number;
    daily_target: number;
    max_capacity: number;
  };
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
  data: AgentMonitorEntry[];
}

// ─── Agent Detail ─────────────────────────────────────────────────────────────

export interface AgentDetailOrder {
  order_id: number;
  order_no?: string; // checkout number the customer quotes (#G1234)
  invoice_no: string;
  order_status: number;
  total_order_amount: string | number;
  cash_on_delivery: boolean;
  pay_later: boolean;
  customer_name: string;
  customer_contact: string;
  order_date: string;
  assigned_at: string | null;
  picked_up_at: string | null;
  delivered_at: string | null;
  delivery_minutes: number | null;
  failed_reason: string | null;
  failed_notes: string | null;
}

export interface AgentDetailData {
  agent_id: number | string;
  full_name: string;
  email: string;
  contact: string;
  address: string;
  nationality: string;
  active: boolean;
  is_online: boolean;
  current_load: number;
  last_latitude: string | null;
  last_longitude: string | null;
  location_updated_at: string | null;
  google_maps_link: string | null;
  joined_date: string;
  recent_orders: AgentDetailOrder[];
  notifications: AgentNotification[];
}

export interface AgentDetailRequest {
  agent_id: number;
}

export interface AgentDetailResponse {
  error: boolean;
  message: string;
  data: AgentDetailData;
}

// ─── Config ───────────────────────────────────────────────────────────────────

export const AGENT_API_ENDPOINTS = {
  AGENT_MONITOR_LIST: '/admin/agent-monitor-list',
  AGENT_DETAIL_LIST:  '/admin/agentdetail-list',
} as const;

// ─── API Slice ────────────────────────────────────────────────────────────────

export const agentDetailsApi = createApi({
  reducerPath: 'agentDetailsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_SERVER_ENDPOINT,
    prepareHeaders: (headers) => {
      headers.set('authkey', '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3');
      const token = localStorage.getItem('admin_token');
      if (token) headers.set('Authorization', `Bearer ${token}`);
      headers.set('Content-Type', 'application/json');
      return headers;
    },
  }),
  tagTypes: ['AgentMonitor', 'AgentDetail'],
  endpoints: (build) => ({

    // ── Agent Monitor List ──
    agentMonitorList: build.query<AgentMonitorListResponse, AgentMonitorListRequest | void>({
      query: (params) => ({
        url:    AGENT_API_ENDPOINTS.AGENT_MONITOR_LIST,
        method: 'POST',
        body:   params || {},
      }),
      providesTags: ['AgentMonitor'],
      transformResponse: (response: any): AgentMonitorListResponse => ({
        error:      response?.error      ?? false,
        message:    response?.message    || '',
        summary:    response?.summary    || { fleet: {}, documents: {}, today: {} },
        settings:   response?.settings   || { sla_minutes: 60, daily_target: 0, max_capacity: 0 },
        pagination: response?.pagination || { total: 0, page: 1, limit: 20, pages: 0 },
        data:       Array.isArray(response?.data) ? response.data : [],
      }),
      transformErrorResponse: (response: any) => ({
        error:   true,
        message: response.data?.message || 'Failed to load agent monitor data',
        summary: { fleet: {}, documents: {}, today: {} },
        settings: { sla_minutes: 60, daily_target: 0, max_capacity: 0 },
        pagination: { total: 0, page: 1, limit: 20, pages: 0 },
        data: [],
      }),
    }),

    // ── Agent Detail ──
    agentDetailList: build.query<AgentDetailResponse, AgentDetailRequest>({
      query: ({ agent_id }) => ({
        url:    AGENT_API_ENDPOINTS.AGENT_DETAIL_LIST,
        method: 'POST',
        body:   { agent_id },
      }),
      providesTags: (_result, _error, { agent_id }) => [
        { type: 'AgentDetail' as const, id: agent_id },
      ],
      transformResponse: (response: any): AgentDetailResponse => ({
        error:   response?.error   ?? false,
        message: response?.message || '',
        data:    response?.data    || null,
      }),
      transformErrorResponse: (response: any) => ({
        error:   true,
        message: response.data?.message || 'Failed to load agent detail',
        data:    null,
      }),
    }),

  }),
});

export const {
  useAgentMonitorListQuery,
  useAgentDetailListQuery,
} = agentDetailsApi;